import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { UploadSimple, FileText, Trash } from '@phosphor-icons/react';

const FileUploader = ({ purpose, label, accept = '.pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt,.csv' }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const load = () => {
    api.get(`/files/mine?purpose=${purpose}`).then((r) => setFiles(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, [purpose]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await api.post(`/files/upload?purpose=${purpose}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${label} uploaded`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/files/${id}`);
      toast.success('Removed');
      load();
    } catch { toast.error('Remove failed'); }
  };

  return (
    <div data-testid={`uploader-${purpose}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow">{label}</div>
        <input ref={inputRef} type="file" accept={accept} onChange={upload} className="hidden" data-testid={`uploader-input-${purpose}`} />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="rounded-none"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`uploader-trigger-${purpose}`}
        >
          <UploadSimple size={14} className="mr-1" /> {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </div>

      {files.length === 0 ? (
        <div className="text-xs text-muted-foreground border border-border border-dashed p-6 text-center">
          No {label.toLowerCase()} yet.
        </div>
      ) : (
        <ul className="space-y-1">
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between border border-border p-2 text-sm" data-testid={`file-row-${f.id}`}>
              <span className="flex items-center gap-2 min-w-0 truncate">
                <FileText size={16} className="text-primary flex-shrink-0" />
                <span className="truncate">{f.original_filename}</span>
                <span className="mono text-xs text-muted-foreground flex-shrink-0">{Math.round((f.size || 0) / 1024)} KB</span>
              </span>
              <button onClick={() => remove(f.id)} className="text-muted-foreground hover:text-destructive" data-testid={`file-delete-${f.id}`}>
                <Trash size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FileUploader;
